// Client-side document prep: the shared backend accepts JPEG/PNG up to 10 MB only, so large photos are
// downscaled/re-encoded and PDFs are rendered to a JPEG image (first pages stacked) before upload.
export const BACKEND_LIMIT = 10 * 1024 * 1024;
const IMAGE_TARGET = 9.5 * 1024 * 1024;
export const WEB_LIMIT = 10 * 1024 * 1024;
export const mb = (n) => `${(n / (1024 * 1024)).toFixed(1)} MB`;

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
  img.src = url;
});

const canvasBlob = (canvas, quality) => new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

export async function compressImage(file, maxBytes = IMAGE_TARGET) {
  const img = await loadImage(file);
  let scale = Math.min(1, 2200 / Math.max(img.width, img.height));
  for (let attempt = 0; attempt < 6; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const q of [0.86, 0.76, 0.66, 0.56]) {
      const blob = await canvasBlob(canvas, q);
      if (blob && blob.size <= maxBytes) return blob;
    }
    scale *= 0.75;
  }
  throw new Error("Image could not be compressed enough");
}

// Returns { base64, mime, name, note } ready for the backend, or throws a user-facing Error.
export async function prepareDocument(file) {
  if (!file || !file.size) throw new Error("The selected file is empty.");
  if (file.size > WEB_LIMIT) throw new Error(`File is ${mb(file.size)}. Please choose a file under 10 MB.`);
  const isImage = /^image\//.test(file.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isImage && !isPdf) throw new Error("Please upload a JPG, PNG or PDF document.");
  if (isPdf) {
    const blob = await pdfToImage(file);
    return { base64: await blobToBase64(blob), mime: "image/jpeg", name: file.name.replace(/\.pdf$/i, "") + ".jpg", note: `PDF converted to an image (${mb(blob.size)}) for upload.` };
  }
  if (file.size <= BACKEND_LIMIT && /^image\/(jpeg|png)$/.test(file.type)) return { base64: await blobToBase64(file), mime: file.type, name: file.name };
  const blob = await compressImage(file); // also converts webp/heic to JPEG
  return { base64: await blobToBase64(blob), mime: "image/jpeg", name: file.name.replace(/\.[^.]+$/, "") + ".jpg", note: `Photo was ${mb(file.size)} — compressed to ${mb(blob.size)} for upload.` };
}

// Render up to 3 PDF pages into one JPEG (stacked vertically). Uses pdf.js lazily.
export async function pdfToImage(file, maxPages = 3) {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = Math.min(doc.numPages, maxPages);
  const scale = 1.6;
  const rendered = [];
  for (let i = 1; i <= pages; i++) {
    const page = await doc.getPage(i);
    const vp = page.getViewport({ scale });
    const c = document.createElement("canvas");
    c.width = Math.round(vp.width); c.height = Math.round(vp.height);
    await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
    rendered.push(c);
  }
  const width = Math.max(...rendered.map((c) => c.width));
  const gap = 24;
  const out = document.createElement("canvas");
  out.width = width; out.height = rendered.reduce((h, c) => h + c.height, 0) + gap * (rendered.length - 1);
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, out.width, out.height);
  let y = 0;
  rendered.forEach((c) => { ctx.drawImage(c, 0, y); y += c.height + gap; });
  for (const q of [0.9, 0.8, 0.7, 0.6]) {
    const blob = await canvasBlob(out, q);
    if (blob && blob.size <= IMAGE_TARGET) return blob;
  }
  throw new Error("This PDF is too large to convert. Please upload a photo of the document instead.");
}
